import Fuse from 'fuse.js';
import { faqDatabase } from '../data/faq-database';

/**
 * FAQ Matcher - Smart question matching using Fuse.js
 * Handles typos, variations, and keyword matching
 */
class FAQMatcher {
  constructor() {
    // Configure Fuse.js for fuzzy search
    this.fuse = new Fuse(faqDatabase, {
      keys: [
        { name: 'question', weight: 0.4 },
        { name: 'keywords', weight: 0.3 },
        { name: 'answer', weight: 0.2 },
        { name: 'category', weight: 0.1 }
      ],
      threshold: 0.4, // 0 = perfect match, 1 = match anything
      distance: 100,
      includeScore: true,
      minMatchCharLength: 3,
      shouldSort: true
    });
  }

  /**
   * Find best matching answer for user question
   * @param {string} userQuestion - The question from user
   * @returns {object} - Match result with answer and metadata
   */
  findBestMatch(userQuestion) {
    // Clean and normalize input
    const cleaned = userQuestion.toLowerCase().trim();
    
    // Handle empty or very short queries
    if (cleaned.length < 3) {
      return {
        success: false,
        message: "Please ask a more specific question.",
        suggestions: this.getPopularQuestions()
      };
    }

    // Search using Fuse.js
    const results = this.fuse.search(cleaned);

    // Check if we have good matches
    if (results.length > 0 && results[0].score < 0.4) {
      const bestMatch = results[0].item;
      const confidence = Math.round((1 - results[0].score) * 100);

      return {
        success: true,
        answer: bestMatch.answer,
        question: bestMatch.question,
        confidence: confidence,
        category: bestMatch.category,
        cta: bestMatch.cta,
        relatedQuestions: this.getRelatedQuestionsData(bestMatch.relatedQuestions),
        suggestions: results.slice(1, 4).map(r => r.item.question)
      };
    }

    // No good match found
    return {
      success: false,
      message: "I couldn't find a specific answer to that question. Here are some related topics:",
      suggestions: this.getSuggestionsFromPartialMatch(cleaned),
      fallback: true
    };
  }

  /**
   * Get related questions data
   * @param {array} relatedIds - Array of related question IDs
   * @returns {array} - Array of related questions
   */
  getRelatedQuestionsData(relatedIds) {
    if (!relatedIds || relatedIds.length === 0) return [];
    return relatedIds
      .map(id => faqDatabase.find(q => q.id === id))
      .filter(Boolean)
      .slice(0, 3)
      .map(q => q.question);
  }

  /**
   * Get suggestions based on partial match
   * @param {string} query - User query
   * @returns {array} - Array of suggested questions
   */
  getSuggestionsFromPartialMatch(query) {
    // Search with more lenient threshold
    const results = this.fuse.search(query, { limit: 5 });
    
    if (results.length > 0) {
      return results.map(r => r.item.question);
    }

    // Return popular questions as fallback
    return this.getPopularQuestions();
  }

  /**
   * Get popular/frequently asked questions
   * @returns {array} - Array of popular questions
   */
  getPopularQuestions() {
    return [
      "How do you structure your pricing?",
      "Which industries do you serve?",
      "What is Lean Manufacturing?",
      "How much does Six Sigma Implementation cost?",
      "What is the ROI timeline for automation projects?",
      "Which ISO standards do you specialize in?",
      "How do I get started with your services?",
      "What does Gen AI & Agentic AI Strategy cost?"
    ];
  }

  /**
   * Get questions by category
   * @param {string} category - Category name
   * @returns {array} - Array of questions in category
   */
  getQuestionsByCategory(category) {
    return faqDatabase
      .filter(faq => faq.category === category)
      .map(faq => faq.question)
      .slice(0, 5);
  }

  /**
   * Get all categories
   * @returns {array} - Array of category objects
   */
  getCategories() {
    const categories = [...new Set(faqDatabase.map(faq => faq.category))];
    return categories.map(cat => ({
      id: cat,
      name: this.getCategoryDisplayName(cat),
      icon: this.getCategoryIcon(cat)
    }));
  }

  /**
   * Get display name for category
   * @param {string} category - Category ID
   * @returns {string} - Display name
   */
  getCategoryDisplayName(category) {
    const names = {
      process: 'Process Excellence',
      compliance: 'Compliance & Risk',
      technology: 'AI & Automation',
      pricing: 'Pricing & Investment',
      general: 'General Information'
    };
    return names[category] || category;
  }

  /**
   * Get icon for category
   * @param {string} category - Category ID
   * @returns {string} - Emoji icon
   */
  getCategoryIcon(category) {
    const icons = {
      process: '⚙️',
      compliance: '🛡️',
      technology: '🤖',
      pricing: '💰',
      general: '📋',
      about: '👤',
      services: '⚙️',
      community: '👥'
    };
    return icons[category] || '📌';
  }

  /**
   * Find matching questions for real-time suggestions (Google-style)
   * @param {string} query - User's partial input
   * @param {number} limit - Maximum number of suggestions (default 6)
   * @returns {array} - Array of suggestion objects with question, score, and category
   */
  findMatchingQuestions(query, limit = 6) {
    // Don't show suggestions for very short queries
    if (!query || query.trim().length < 2) {
      return [];
    }

    const cleaned = query.toLowerCase().trim();

    // Create a more lenient Fuse instance for suggestions
    const suggestionFuse = new Fuse(faqDatabase, {
      keys: [
        { name: 'question', weight: 0.5 },
        { name: 'keywords', weight: 0.3 },
        { name: 'category', weight: 0.2 }
      ],
      threshold: 0.5, // More lenient for suggestions
      distance: 100,
      includeScore: true,
      minMatchCharLength: 2,
      shouldSort: true
    });

    // Search and format results
    const results = suggestionFuse.search(cleaned);
    
    return results.slice(0, limit).map(result => ({
      question: result.item.question,
      score: Math.round((1 - result.score) * 100), // Convert to percentage
      category: result.item.category,
      categoryDisplay: this.getCategoryDisplayName(result.item.category),
      id: result.item.id
    }));
  }
}

// Export singleton instance
export const faqMatcher = new FAQMatcher();
export default faqMatcher;
